import { contains } from 'ramda';
import React, { FC } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { nest } from 'recompose';
import { RootModal } from '../../components/common/RootModal';
import { SettingsLayout } from '../../constants/Layout';
import { RoutePath } from '../../constants/Routes';
import { MainSource, SupportedExternalSourcesList } from '../../constants/Source';
import { getQuery } from '../../critical/utils';
import { parentConnection, ParentConnectionEvent } from '../../services/parentConnection';
import { useAnalyticsProvider } from '../../utils/hooks/bi/useAnalyticsProvider';
import { AudioPlaybackProvider } from '../../utils/hooks/useAudioPlayback';
import { DropOnSidebarProvider } from '../../utils/hooks/useDropOnSidebar';
import { FeatureOnboardingProvider } from '../../utils/hooks/useFeatureOnboarding';
import { FilesUploaderProvider } from '../../utils/hooks/useFilesUploader';
import { RedirectAfterFirstFileUploadedProvider } from '../../utils/hooks/useFilesUploader/useRedirectAfterFirstFileUploaded';
import { GetItemPriceProvider } from '../../utils/hooks/useGetItemPrice';
import { MediaStudioProvider } from '../../utils/hooks/useMediaStudio';
import { ModalOptionsProvider } from '../../utils/hooks/useModal';
import { NotificatorProvider } from '../../utils/hooks/useNotificator';
import { PurchaseMediaProvider } from '../../utils/hooks/usePurchaseMedia';
import { ScrollStreamProvider } from '../../utils/hooks/useScrollStream';
import { SpaceStorageToastsProvider } from '../../utils/hooks/storageQuota/useStorageSpaceToasts';
import { UserFeedbackProvider } from '../../utils/hooks/useUserFeedback';
import { VideoMakerProvider } from '../../utils/hooks/useVideoMaker';
import { AdobeExpressProvider } from '../../utils/hooks/useAdobeExpress';
import { ClipboardProvider } from '../../utils/hooks/useClipboard/useClipboard';
import { getRootFolderIdForSource } from '../../utils/media-manager-sdk/root-folder';
import { isAllSource } from '../../utils/media-manager-sdk/sources';
import { RouteBuilder } from '../../utils/RouteHelpers';
import { EmptyState } from '../FilesManager/EmptyState';
import { LocaleProvider } from '../LocaleProvider/LocaleProvider';
import { StorageQuotaProvider } from '../../utils/hooks/storageQuota/useStorageQuota';
import { Root } from '../Root';
import { ErrorCatcher } from './ErrorCatcher';
import { SearchResultsBIProvider } from '../FilesManager/SearchResults/useSearchResultsBI';

import '@wix/design-system/styles.global.css';

const renderEnterpriseTab = ({
  history,
  location: { search },
  match: {
    params: { tab, folderId },
  },
}: any) => {
  const { searchQuery = '', searchCacheKiller = '', openedExternal } = getQuery(search);
  return (
    <Root
      source={MainSource.Enterprise}
      folderId={folderId || getRootFolderIdForSource(MainSource.Enterprise)}
      searchQuery={searchQuery}
      searchCacheKiller={searchCacheKiller}
      navigateTo={history.push}
      openedExternal={openedExternal}
      enterpriseTab={tab}
    />
  );
};

// TODO: Add types to params
const renderRoot = ({
  history,
  location: { search },
  match: {
    params: { source = MainSource.SiteFiles, folderId },
  },
}: any) => {
  const { searchQuery = '', searchCacheKiller = '', openedExternal } = getQuery(search);

  if (isAllSource(source) && !searchQuery) {
    return <Redirect to={RouteBuilder.Source(MainSource.SiteFiles)} />;
  }

  return (
    <Root
      source={source}
      folderId={folderId || getRootFolderIdForSource(source)}
      searchQuery={searchQuery}
      searchCacheKiller={searchCacheKiller}
      navigateTo={history.push}
      openedExternal={openedExternal}
    />
  );
};

const renderExternal = ({
  match: {
    params: { externalSource },
  },
}: any) => (
  <Redirect
    to={{
      pathname: RouteBuilder.Source(MainSource.SiteFiles),
      search: contains(externalSource, SupportedExternalSourcesList) ? `?openedExternal=${externalSource}` : '',
    }}
  />
);

const renderShowAll = ({ history }) => <Root source={MainSource.SiteFiles} navigateTo={history.push} />;

const renderUnknownError = () => (
  <RootModal onCancel={() => parentConnection.emit(ParentConnectionEvent.Close)}>
    <EmptyState
      isUnknownError
      source={MainSource.SiteFiles}
      settingsLayout={SettingsLayout.GalleryWithPanel}
      dataHook="files-manager-empty-state"
    />
  </RootModal>
);

const Providers = nest(
  LocaleProvider,
  NotificatorProvider,
  StorageQuotaProvider,
  SearchResultsBIProvider,
  AudioPlaybackProvider,
  ModalOptionsProvider,
  MediaStudioProvider,
  UserFeedbackProvider,
  VideoMakerProvider,
  ScrollStreamProvider,
  GetItemPriceProvider,
  PurchaseMediaProvider,
  FeatureOnboardingProvider,
  RedirectAfterFirstFileUploadedProvider,
  FilesUploaderProvider,
  DropOnSidebarProvider,
  SpaceStorageToastsProvider,
  AdobeExpressProvider,
  ClipboardProvider,
);

export const InnerApp: FC = () => {
  useAnalyticsProvider();

  return (
    <ErrorCatcher>
      <Providers>
        <Switch>
          <Route path={RoutePath.EnterpriseTab} render={renderEnterpriseTab} />
          <Route path={RoutePath.Empty} render={renderRoot} />
          <Route path={RoutePath.Search} render={renderRoot} />
          <Route path={RoutePath.ShowAll} render={renderShowAll} />
          <Route path={RoutePath.UnknownError} render={renderUnknownError} />
          <Route path={RoutePath.Source} render={renderRoot} />
          <Route path={RoutePath.External} render={renderExternal} />
          <Route path={RoutePath.Home} render={renderRoot} />
          <Redirect to={RouteBuilder.Source(MainSource.SiteFiles)} />
        </Switch>
      </Providers>
    </ErrorCatcher>
  );
};
